import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Filter, MapPin, Home, Square, Calendar } from 'lucide-react';
import { mockProjects } from '../mock';
import ProjectModal from './ProjectModal';

const ProjectsPage = () => {
  const location = useLocation();
  // Project name (e.g., "Natpa") passed from homepage/modal ad via state, or undefined.
  const showProjectFromState = location.state?.showProject || null;

  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState(mockProjects);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');

  const projectTypes = ['All', 'Residential'];
  const statusTypes = ['All', 'Available', 'Under Construction', 'Sold Out'];

  // Handle ad/route-based modal open by project name
  useEffect(() => {
    if (showProjectFromState) {
      const projectObj = mockProjects.find(
        p => p.title.trim().toLowerCase() === showProjectFromState.trim().toLowerCase()
      );
      if (projectObj) {
        setSelectedProject(projectObj);
        setIsModalOpen(true);
      }
    }
    // eslint-disable-next-line
  }, [showProjectFromState]);

  const handleProjectClick = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    filterProjects(term, selectedFilter);
  };

  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    filterProjects(searchTerm, filter);
  };

  const filterProjects = (search, filter) => {
    let filtered = mockProjects;

    if (search) {
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(search.toLowerCase()) ||
        project.location.toLowerCase().includes(search.toLowerCase()) ||
        project.type.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filter !== 'All') {
      filtered = filtered.filter(project =>
        project.type === filter || project.status === filter
      );
    }

    setFilteredProjects(filtered);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Under Construction':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Sold Out':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-yellow-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold" style={{ color: "#ca8a04" }}>
              Our Projects
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our portfolio of premium residential and commercial projects 
              across Chennai's most desirable locations.
            </p>
          </div>
          {/* Search and Filter Bar */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by project name, location, or type..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all duration-200"
                />
              </div>
              {/* Filter Buttons */}
              <div className="flex flex-wrap gap-2">
                {[...projectTypes, ...statusTypes.slice(1)].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => handleFilterChange(filter)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      selectedFilter === filter
                        ? 'bg-yellow-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-12 w-12 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">No Projects Found</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Try adjusting your search terms or filters to find what you're looking for.
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-800">
                  {filteredProjects.length} {filteredProjects.length === 1 ? 'Project' : 'Projects'} Found
                </h2>
                <div className="flex items-center text-gray-600">
                  <Filter className="h-5 w-5 mr-2" />
                  <span>Filtered by: {selectedFilter}</span>
                </div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredProjects.map((project) => (
                  <div
                    key={project.id}
                    onClick={() => handleProjectClick(project)}
                    className="group bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 overflow-hidden cursor-pointer transform hover:scale-105 hover:shadow-xl"
                  >
                    <div className="aspect-video bg-gray-200 overflow-hidden relative">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="text-sm font-medium" style={{ color: "#ca8a04", background: "white", opacity: 0.9, padding: "0.25rem 0.75rem", borderRadius: "9999px"}}>
                          {project.type}
                        </span>
                      </div>
                      <div className="absolute top-4 right-4">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full border ${getStatusColor(project.status)}`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-yellow-600 transition-colors duration-200">
                        {project.title}
                      </h3>
                      <div className="flex items-center text-gray-600 mb-4">
                        <MapPin className="h-4 w-4 mr-2" style={{ color: "#ca8a04" }} />
                        <span className="text-sm">{project.location}</span>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-gray-600">
                          <Home className="h-4 w-4 mr-2" style={{ color: "#ca8a04" }} />
                          <span>{project.bhk}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <Square className="h-4 w-4 mr-2" style={{ color: "#ca8a04" }} />
                          <span>{project.area}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold text-gray-800">{project.price}</span>
                        <button className="text-yellow-600 hover:text-yellow-700 font-medium text-sm px-4 py-2 rounded-lg hover:bg-yellow-50 transition-all duration-200">
                          View Details →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Project Modal */}
      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default ProjectsPage;
